import React, { useState, useEffect, useMemo, useRef } from "react";

import { Add as AddIcon } from "@mui/icons-material";
import { SelectChangeEvent, Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
  RowActionsExtra,
  Icons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { RiUserSettingsLine } from "react-icons/ri";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../components/Form/Button";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Row } from "../../../../../../models/shipeezi.table";
import { Roles as IRoles, User } from "../../../../../../models/user";
import { Status, RoleType } from "../../../../../../util/enums";
import useStyles, { ButtonText } from "./styles";
import useActions from "./useActions";
import Select from "Components/Form/Select";
import MenuItem from "@mui/material/MenuItem";
import { AccessRuleDrawer, AccessRuleDrawerHandles } from "./Modal/AccessRuleDrawer";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { IconTypo } from "../../../../../../modules/moving/components/Diary/views/DiaryResourcesView/subcomponent/Viewers/ResourceTaskViewer/TaskCard/styles";
import { format } from "date-fns";
import { CreateRoleModal } from "./Modal/CreateRoleModal";
import { AssignUsersToRoleModal } from "./Modal/AssignUsersToRoleModal";
import { isMoving } from "Util/validation";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

const Roles = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const {
    roles,
    hasErrors,
    total,
    page,
    pageSize,
    isLoading,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    duplicateRole,
    showAllStatusCode,
    updateUser,
    refetch,
    refetchRolesTotal,
    updateRole,
  } = useActions();

  const [selectedRole, setSelectedRole] = useState<IRoles>();
  const [openAssignModal, setOpenAssignModal] = useState<boolean>(false);
  const [openCreateRoleModal, setOpenCreateRoleModal] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>(showAllStatusCode);
  const { currentUser, userPermissions } = useAppState();
  const accessRuleDrawerRef = useRef<AccessRuleDrawerHandles>(null);
  const [accessRulePermission, setAccessRulePermission] = useState<{
    edit: boolean;
    create: boolean;
  }>({
    edit: false,
    create: false,
  });
  const editIcon = require("Assets/icons/edit-new.svg");
  const eyeIcon = require("Assets/icons/eye-new.svg");

  const accessRuleStatus: { value: string; name: string }[] = useMemo(
    () => [
      { value: showAllStatusCode, name: translate(showAllStatusCode) },
      { value: Status.ACTIVE, name: translate(Status.ACTIVE) },
      { value: Status.INACTIVE, name: translate(Status.INACTIVE) },
    ],
    []
  );

  const handleFilterBy = (value: string) => {
    let filterValue: string[] = [];
    setFilterValue(value);
    if (value === showAllStatusCode) {
      filterValue = [Status.ACTIVE, Status.INACTIVE];
    } else {
      filterValue?.push(value);
    }
    handleFilter({ status: filterValue });
  };

  const editRole = (index: number) => {
    const isTypeRoleSystem: boolean =
      roles?.find((role) => role.id === index)?.type === RoleType.SYSTEM;

    return (
      <Tooltip title={translate("edit") || ""} placement="top">
        <div>
          <Icons.edit
            data-cy="action-edit"
            size={18}
            color={`${isTypeRoleSystem ? "#00000042" : "#475467"}`}
            className={`${!isTypeRoleSystem && classes.button}`}
            onClick={() => {
              if (!isTypeRoleSystem) {
                if (hasErrors !== undefined) setHasErrors(undefined);
                const roleSelected = roles?.find((role) => role.id === index);

                setSelectedRole(roleSelected);
                setOpenCreateRoleModal(true);
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const assignUsersToRole = (index: number) => {
    const foundRole = roles?.find((role) => role.id === index);
    const isRoleInactive: boolean = foundRole?.status === Status.INACTIVE;

    return (
      <Tooltip title={translate("assignUser") || ""} placement="top">
        <div>
          <RiUserSettingsLine
            data-cy="action-assign"
            size={18}
            className={`${!isRoleInactive && classes.button}`}
            color={`${isRoleInactive ? "#00000042" : "#475467"}`}
            onClick={() => {
              if (!isRoleInactive) {
                if (hasErrors !== undefined) setHasErrors(undefined);
                setSelectedRole(foundRole);
                setOpenAssignModal(true);
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const changeStatusOfRole = (index: number) => {
    const roleToUpdate = roles?.find((role) => role.id === index);
    const isTypeRoleSystem: boolean =
      roles?.find((role) => role.id === index)?.type === RoleType.SYSTEM;

    return (
      <Tooltip title={translate("changeStatus") || ""} placement="top">
        <div>
          <GridIcons.status
            data-cy="action-change"
            color={`${isTypeRoleSystem ? "#00000042" : "#475467"}`}
            className={`${!isTypeRoleSystem && classes.button}`}
            onClick={() => {
              if (roleToUpdate && roleToUpdate?.status && !isTypeRoleSystem) {
                updateRole({
                  id: index,
                  role: {
                    name: roleToUpdate.name,
                    companyId: roleToUpdate.companyId,
                    status:
                      roleToUpdate.status === Status.INACTIVE ? Status.ACTIVE : Status.INACTIVE,
                  } as IRoles,
                });
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const renderUpdateAccessRuleBtn = (accessRuleId: string | number, accessRule: IRoles) => {
    if (accessRule?.type === RoleType.CUSTOM) {
      return (
        <Tooltip data-cy="action-edit" title={translate("edit")} placement="top">
          <div>
            <Icons.edit
              className={classes.button}
              size={18}
              color="#475467"
              onClick={() => {
                accessRuleDrawerRef.current?.open(accessRule);
              }}
            />
          </div>
        </Tooltip>
      );
    }
  };

  const renderViewAccessRuleBtn = (accessRuleId: string | number, accessRule: IRoles) => {
    if (accessRule?.type === RoleType.SYSTEM) {
      return (
        <Tooltip data-cy="action-edit" title={translate("view")} placement="top">
          <div>
            <Icons.view
              className={classes.button}
              size={18}
              color="#475467"
              onClick={() => {
                accessRuleDrawerRef.current?.open(accessRule);
              }}
            />
          </div>
        </Tooltip>
      );
    }
  };

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : classes.statusBadgeInactive}
      `}
    >
      {status}
    </Badge>
  );

  const columns: Column[] = [
    {
      name: "name",
      title: translate("accessRule"),
      formatComponent: (name) => <span className={classes.rowStyle}>{name}</span>,
    },
    {
      name: "type",
      title: translate("type"),
      hideSearch: true,
      formatComponent: (type) => (
        <span className={classes.rowStyle}>
          {type === RoleType.SYSTEM ? `${RoleType.SYSTEM} default` : type}
        </span>
      ),
    },
    {
      name: "qty",
      title: translate("groups"),
      hideSort: true,
      hideSearch: true,
      formatComponent: (qty) => <span className={classes.rowStyle}>{qty}</span>,
    },
    {
      name: "updatedAt",
      title: translate("lastModified"),
      formatComponent: (updateAt) => (
        <span className={classes.rowStyle}>
          {updateAt && currentUser?.preferences
            ? formatDateWithUserPref(updateAt, currentUser.preferences)
            : ""}
        </span>
      ),
      hideSearch: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: any) => badgeStatus(status),
      hideSearch: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "filterBy"],
      right: ["newRecord", "handleNewRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    filterBy: (
      <Select
        className={classes.selectStatus}
        tabIndex={0}
        name="status"
        value={filterValue}
        onChange={(e: SelectChangeEvent<any>) => handleFilterBy(e?.target?.value)}
      >
        {accessRuleStatus?.map((status) => (
          <MenuItem className={classes.selectStatusItem} key={status.value} value={status.value}>
            {status.name}
          </MenuItem>
        ))}
      </Select>
    ),
    handleNewRecord: !isMoving(currentUser?.company)
      ? {
          text: translate("createNewRole"),
          action: () => {
            if (hasErrors !== undefined) setHasErrors(undefined);
            setSelectedRole(undefined);
            setOpenCreateRoleModal(true);
          },
          tooltip: translate("createNewRole"),
        }
      : undefined,
    newRecord: isMoving(currentUser?.company) &&
      isShowAction(currentUser, accessRulePermission.create) && (
        <Button
          className={classes.newRecord}
          customColor={Color.SECONDARY_DARK}
          startIcon={<AddIcon />}
          onClick={() => {
            if (isMoving(currentUser?.company)) {
              accessRuleDrawerRef.current?.open({} as IRoles);
            } else {
              setSelectedRole(undefined);
              setOpenCreateRoleModal(true);
            }
          }}
        >
          <ButtonText>{translate("addNew")}</ButtonText>
        </Button>
      ),
  };

  const handlerowActionsExtraByPermission = () => {
    let rows: RowActionsExtra[] = [];
    if (isMoving(currentUser?.company)) {
      rows?.push({
        title: "View",
        key: "viewAccessRule",
        buttonAndAction: renderViewAccessRuleBtn,
      });
      if (isShowAction(currentUser, accessRulePermission.edit)) {
        rows?.push({
          title: "Edit",
          key: "updateAccessRule",
          buttonAndAction: renderUpdateAccessRuleBtn,
        });
      }
    } else {
      rows?.push(
        { title: "Edit", key: "updateAccessRule", buttonAndAction: editRole },
        {
          title: translate("assignRole"),
          key: "handleAssign",
          buttonAndAction: assignUsersToRole,
        },
        {
          title: translate("changeStatus"),
          key: "handleChangeRole",
          buttonAndAction: changeStatusOfRole,
        }
      );
    }

    return rows;
  };

  const rowActionsExtra = handlerowActionsExtraByPermission();

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setAccessRulePermission({
        edit:
          checkResourceCan(userPermissions, ResourceName.PEOPLE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SETTINGS_PEOPLE_ACCESS_RULES_EDIT),
        create:
          checkResourceCan(userPermissions, ResourceName.PEOPLE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SETTINGS_PEOPLE_ACCESS_RULES_ADD_NEW),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    return () => {
      handleFilter({ status: [] });
    };
  }, []);

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request-notification-success-role-changes",
      });
      setHasErrors(undefined);
    }
  }, [hasErrors]);

  return (
    <div data-cy="peopleDataList" className={classes.gridWrapper}>
      <ShipeeziNewGrid
        data={roles}
        columns={columns}
        keyExtractor={(data) => data.id}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["updateAccessRule", "viewAccessRule", "handleAssign", "handleChangeRole"],
        }}
        rowActionsExtra={rowActionsExtra}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      <AccessRuleDrawer ref={accessRuleDrawerRef} />
      {openAssignModal && (
        <AssignUsersToRoleModal
          isOpen={openAssignModal}
          closeModal={() => setOpenAssignModal(false)}
          selectedRole={selectedRole}
          isLoadingUpdate={isLoading}
          updateRole={(roleId: number | null, userId: number) =>
            updateUser({
              id: userId,
              user: { roleId } as User,
            })
          }
          refetchRoleList={refetch}
          refetchRolesListCount={refetchRolesTotal}
        />
      )}
      {openCreateRoleModal && (
        <CreateRoleModal
          roleToBeEditId={selectedRole?.id}
          isOpen={openCreateRoleModal}
          closeModal={() => setOpenCreateRoleModal(false)}
          refreshList={refetch}
        />
      )}
    </div>
  );
};

export default Roles;
